import Cookies from "js-cookie"
import { html, render } from "lit-html"
import { repeat } from "lit-html/directives/repeat"
import { virtual, useEffect, useState } from "haunted"
import { debounce, formToObj } from "../utils"
import { ALT_SORT, DEBOUNCE_TIME } from "../constants"
import { addToastMessage } from "../generic/messages"
import AgencyRow from "./agency"
import AssignmentRow from "./assignment"
import DefaultRow from "./default"
import DocumentRow from "./document"
import MeetingRow from "./meeting"
import WorkshopRow from "./workshop"
import UserRow from "./user"
import ReportingRow from "./reporting"
import NoResults from "./no-results"
import Toggle from "./toggle"

const rowComponent = (template, props) => {
  switch (template) {
    case "agency":
    case "agencies":
      return AgencyRow(props)
    case "assignment":
    case "assignments":
      return AssignmentRow(props)
    case "document":
    case "documents":
      return DocumentRow(props)
    case "meeting":
    case "meetings":
      return props.assignment
        ? AssignmentRow({ ...props, ...props.assignment })
        : MeetingRow(props)
    case "workshop":
    case "workshops":
      return WorkshopRow(props)
    case "user":
    case "users":
      return UserRow(props)
    case "reporting":
      return ReportingRow(props)
    default:
      return DefaultRow(props)
  }
}

const setupMobileToggle = (toggleButton) => {
  const toggleBody = document.getElementById(
    toggleButton.getAttribute("aria-controls")
  )

  const updateVisibility = () => {
    const isMobile = window.getComputedStyle(toggleButton).display !== "none"
    const hasMobileHiddenClass = toggleBody.classList.contains("hidden")
    toggleButton.setAttribute(
      "aria-expanded",
      (!isMobile || (isMobile && !hasMobileHiddenClass)).toString()
    )
  }

  const onResize = debounce(updateVisibility, DEBOUNCE_TIME)
  window.addEventListener("resize", onResize)

  toggleButton.addEventListener("click", () => {
    toggleBody.classList.toggle("hidden")
    updateVisibility()
  })

  updateVisibility()
}

const clearFilters = (el) => {
  el.querySelector(`form[name="filter"]`).reset()
  el.querySelectorAll(
    `input[name="search"], input[type="hidden"], select`
  ).forEach((el) => (el.value = ``))
  el.querySelectorAll("input:checked").forEach((checked) => {
    checked.checked = false
  })
  el.querySelectorAll(`.tag-filter-input.tags-checked`).forEach((el) => {
    el.classList.remove("tags-checked")
  })
  el.querySelectorAll(`input[data-has-value]`).forEach((el) => {
    el.dispatchEvent(new Event("change"))
  })
}

const getFormDataObj = (el) => formToObj(el.querySelector("form"))

const getQueryStr = (formData) => {
  // Remove blank values from form object
  Object.entries(formData).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      formData[key] = value.filter((v) => !(typeof v === "string" && !v.trim()))
    }
    if (value === "" || value === []) delete formData[key]
  })
  return `${new URLSearchParams(formData)}`
}

const sendGaFilterEvent = (queryStr) => {
  if (!window.gtag) {
    return
  }
  window.gtag("event", "filter", {
    event_category: window.location.pathname,
    event_label: queryStr,
    anonymize_ip: true,
  })
}

const updateWindowQueryStr = (queryStr) => {
  window.history.replaceState(
    {},
    window.document.title,
    `${window.location.protocol}//${window.location.host}${window.location.pathname}?${queryStr}`
  )
  sendGaFilterEvent(queryStr)
}

const updateResultsInfo = ({ count, previous }) => {
  const countEl = document.getElementById("filter-results-count")
  if (countEl && count !== null && count !== undefined) {
    countEl.innerText = `${count.toLocaleString()} results`
  }
  const statusEl = document.getElementById("filter-status")
  // Only update status el if previous is undefined, meaning there's a new filter and
  // not that the user has clicked "Load more"
  if (statusEl && count !== null && count !== undefined && previous === null) {
    statusEl.innerText = `There are ${count} results available`
  }
}

const hasActiveFilters = (formData) =>
  // eslint-disable-next-line no-unused-vars
  Array.from(Object.entries(formData)).some(([key, value]) => value !== "")

const loadResults = ({ endpoint, queryStr, page, csrftoken }) =>
  fetch(`${endpoint}?${queryStr}&page=${page}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "X-CSRFToken": csrftoken,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  })
    .then((res) => res.json())
    .catch((err) => console.error(err))

export const FilterSet = virtual(
  ({
    el,
    csrftoken,
    rowTemplate,
    endpoint,
    noResultsMessage,
    defaultLabel,
    altLabel,
  }) => {
    const [results, setResults] = useState([])
    const [page, setPage] = useState(1)
    const [didMount, setDidMount] = useState(false)
    const [loading, setLoading] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [queryStr, setQueryStr] = useState(getQueryStr(getFormDataObj(el)))
    const [altSortField, setAltSortField] = useState(``)

    // Set didMount to control which effects run on initial render
    useEffect(() => setDidMount(true), [])

    const clearFilterButtons = document.querySelectorAll(
      "button[data-action='clear']"
    )

    const onClear = () => {
      clearFilters(el)
      setQueryStr(getQueryStr(getFormDataObj(el)))
    }

    // Because of how it functions, we're handling the alt sort input separately
    const formObj = getFormDataObj(el)
    const setAltSort = (altSort) => {
      const altInput = el.querySelector(`[name="${altSortField}"]`)
      altInput.value = altSort ? "true" : ""
      setQueryStr(getQueryStr(getFormDataObj(el)))
    }

    // Setup initial event listeners on page load
    useEffect(() => {
      el.querySelectorAll(`[data-action="filter-fields-body-toggle"]`).forEach(
        setupMobileToggle
      )
      el.querySelector(`form[name="filter"]`)
        .querySelectorAll("input, select")
        .forEach((input) => {
          input.addEventListener(
            input.type === "text" ? "input" : "change",
            debounce(
              () => setQueryStr(getQueryStr(getFormDataObj(el))),
              DEBOUNCE_TIME
            )
          )
        })

      clearFilterButtons.forEach((btn) => {
        btn.classList.toggle("hidden", !hasActiveFilters(getFormDataObj(el)))
        btn.addEventListener("click", onClear)
      })

      const altSortInput = el.querySelector(`[data-type="${ALT_SORT}"]`)
      if (altSortInput) {
        setAltSortField(altSortInput.name)
      }
    }, [el])

    // Update displayed results based on current filters, endpoint and page
    const updateResults = () => {
      // A -1 page is just to trigger an update, reset this first without requesting
      if (page < 1) {
        setPage(1)
        return
      }
      setLoading(true)
      loadResults({
        endpoint,
        queryStr,
        page,
        csrftoken,
      })
        .then((res) => {
          setLoading(false)
          setHasMore(!!res.next)
          setResults(page === 1 ? res.results : [...results, ...res.results])
          updateResultsInfo(res)
        })
        .catch((e) => {
          console.error(e)
          addToastMessage("There was an error finding your results", "error")
        })
    }

    // Whenever the page changes, update the results
    useEffect(updateResults, [page])

    // Whenever the filters or endpoint changes, reset the page and window query string
    useEffect(() => {
      // Don't run on initial render because initial updateResults already run
      if (!didMount) return
      // Set page as -1 to make sure update is triggered
      setPage(-1)
      updateWindowQueryStr(queryStr)
      // Hide filter buttons if no active filters
      clearFilterButtons.forEach((btn) => {
        btn.classList.toggle("hidden", !hasActiveFilters(getFormDataObj(el)))
      })
    }, [queryStr])

    const noResults = loading
      ? ``
      : NoResults({
          message: noResultsMessage,
          onClear,
        })

    return html`
      <div class="relative loader-container ${loading ? "loading" : ""}">
        ${Toggle({
          defaultLabel,
          altLabel,
          altSort: !!formObj[altSortField],
          onToggle: setAltSort,
        })}
        <div data-section="filter-results" class="bg-white mb-7">
          ${repeat(
            results,
            (row) => row.id,
            (row) =>
              rowComponent(rowTemplate, {
                ...row,
                onUpdate: () => setPage(-1),
              })
          )}
          ${results.length === 0 ? noResults : ``}
        </div>
        ${hasMore
          ? html`
              <div class="flex mx-5 mt-2">
                <button
                  class="button h-10 text-center m-0 grow-1 rounded-xxl ${loading
                    ? "loading"
                    : ""}"
                  type="button"
                  @click=${() => setPage(page + 1)}
                >
                  Load more
                </button>
              </div>
            `
          : ""}
      </div>
    `
  }
)

export const renderFilterSet = (el) => {
  const {
    row_template,
    no_results_message,
    endpoint,
    default_label,
    alt_label,
  } = JSON.parse(
    document.querySelector(`[type="application/json"][data-props]`).innerText
  )

  const csrftoken = Cookies.get("csrftoken")
  const searchParams = new URLSearchParams(window.location.search)

  render(
    FilterSet({
      el,
      csrftoken,
      rowTemplate: row_template,
      endpoint,
      noResultsMessage: no_results_message,
      defaultLabel: default_label,
      altLabel: alt_label,
      isAlt: searchParams.get("alt"),
    }),
    document.getElementById("filter-mount")
  )
}
